import * as Xstate from "@xstate/react";
import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebounce } from "usehooks-ts";

import { GlobalContext } from "../../../../../app/stateMachines/GlobalContext";
import { AvatarItem } from "../../../../../components/newUI/avatar";
import { RoleMembersPreview } from "../../../../../components/select/previews/RoleMembersPreview";
import {
  getInitials,
  getInitialsFromFullName,
} from "../../../../../constants/constants";
import { WorkDetailsModel } from "../../../../../factories/work/model/work-details.model";
import { useAppSelector } from "../../../../../redux/redux";
import { getLinkedFirm } from "../../../../../sessionStorage/sessionStorage";
import { useAddAutomationMutation } from "../../../../../slices/AutomationSlice";
import { useLookupClientTeamsQuery } from "../../../../../slices/ClientTeamSlice";
import { useGetActiveCustomersQuery } from "../../../../../slices/ContactsSlice";
import { useGetTagsQuery } from "../../../../../slices/ContactTagsSlice";
import { useStartSequenceMutation } from "../../../../../slices/EmailSequencesSlice";
import { useAddEmailToWorkMutation } from "../../../../../slices/EmailsSlice";
import { useGetRolesQuery } from "../../../../../slices/RoleSlice";
import { useLazySearchTemplateQuery } from "../../../../../slices/TemplatesSlice";
import { useGetUsersQuery } from "../../../../../slices/UserSlice";
import { useAddWorkNoteMutation } from "../../../../../slices/WorkNotesSlice";
import {
  useAddWorkMutation,
  useUpdateWorkRepeatsMutation,
} from "../../../../../slices/WorkSlice";
import {
  useLazyGetWorkTypeQuery,
  useLazySearchWorkTypesQuery,
} from "../../../../../slices/WorkTypeSlice";
import { SelectOptionType } from "../../../../../types/types";
import {
  defaultDueDateFromStart,
  validDueDays,
} from "../../../../../utils/dueDays";
import { serverMessage } from "../../../../../utils/serverMessage";
import { compareSelectOptionsAlphabeticallyWithNumbersFirst } from "../../../../../utils/sort";
import {
  chooseDefaultAssignee,
  sharedManager,
} from "../../../../clientTeam/clientTeam.utils";
import {
  AutomationDraft,
  AutomationItem,
} from "../../../../work/pages/automation/automation.constants";
import {
  getAutomationActionBodyValue,
  getAutomationConditionBodyValue,
} from "../../../../work/pages/automation/automation.helpers";
import { AutomationTarget } from "../../../../work/pages/automation/automation.types";
import { useAutomationLookups } from "../../../../work/pages/automation/useAutomationLookups";
import {
  buildRepeatPayload,
  RecurringScheduleForm,
} from "../../../../work/pages/repeats/recurringSchedule";
import { SequenceForm } from "../../../../work/pages/sequences/emailSequence.types";
import { buildSequencePayload } from "../../../../work/pages/sequences/emailSequence.utils";
import {
  CONTACTS_PAGE_SIZE,
  CONTACTS_SORT_COLUMN,
  CONTACTS_SORT_ORDER,
  NO_ROLE_OPTION,
} from "../model/addWork.constants";
import {
  AddWorkContactOption,
  AddWorkContactTagOption,
  AddWorkFormData,
  AddWorkModalProps,
} from "../model/addWork.types";
import {
  buildAddWorkDto,
  buildDefaultValues,
  buildUserOption,
  findOptionByValue,
  getSelectedAssigneeItems,
  getSelectedRole,
  mapCustomerToContactOption,
  mapLegacyContactToContactOption,
  mapTagCustomerToContactOption,
  uniqueOptionsByValue,
} from "../utils/addWork.helpers";

type UseAddWorkModalControllerParams = AddWorkModalProps;

/** Assignee pre-filled by an Ask ProCharted proposal: never replaced automatically. */
const FIXED = "__fixed";

const getCreatedWorkIds = (response: string[] | { id?: string }): string[] => {
  if (Array.isArray(response)) {
    return response.filter(Boolean);
  }

  return response.id ? [response.id] : [];
};

export const useAddWorkModalController = ({
  isOpen,
  onClose,
  defaultContact,
  defaultContacts,
  selectedMail,
  initialValues,
  initialNote,
  onCreated,
  initialRepeat,
  initialAutomation,
  initialSequence,
}: UseAddWorkModalControllerParams) => {
  const navigate = useNavigate();
  const orgId = getLinkedFirm()?.orgId || "";
  const user = useAppSelector((state) => state.appReducer.user);
  const { userInfoService } = useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const userInfoByEmail = userInfoState.context.userInfoByEmail;
  const [addWorkNote] = useAddWorkNoteMutation();
  const [noteDraft, setNoteDraft] = useState(initialNote || "");

  const [addWork, { isLoading: isAddingWork }] = useAddWorkMutation();
  const [addWorkToEmail] = useAddEmailToWorkMutation();
  const [addAutomation, { isLoading: isAddingAutomation }] =
    useAddAutomationMutation();

  const [searchContacts, setSearchContacts] = useState("");
  const [searchType, setSearchType] = useState("");
  const [searchTemplate, setSearchTemplate] = useState("");
  const [contactsPage, setContactsPage] = useState(1);
  const [contactOptions, setContactOptions] = useState<AddWorkContactOption[]>(
    [],
  );
  const [contactSelectTab, setContactSelectTab] = useState<"contacts" | "tags">(
    "contacts",
  );
  const [selectedContactTag, setSelectedContactTag] =
    useState<AddWorkContactTagOption | null>(null);
  const [isInvalidDatesModalOpen, setIsInvalidDatesModalOpen] = useState(false);
  const [selectedAutomationTarget, setSelectedAutomationTarget] =
    useState<AutomationTarget | null>(null);
  const [automationDraft, setAutomationDraft] =
    useState<AutomationDraft | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [sequenceDraft, setSequenceDraft] = useState<SequenceForm | null>(null);
  const [repeatDraft, setRepeatDraft] = useState<RecurringScheduleForm | null>(
    null,
  );
  const [updateWorkRepeats, { isLoading: isStartingRepeat }] =
    useUpdateWorkRepeatsMutation();
  const [startSequence, { isLoading: isStartingSequence }] =
    useStartSequenceMutation();
  const isLoadingNextContactsPageRef = useRef(false);
  const defaultWorkTypeRequestIdRef = useRef(0);
  // Work type's "Default due date: N days after start" (Roadmap 1849). The due date
  // follows start + N until the person picks a due date themselves (or one was
  // pre-filled, e.g. by an Ask ProCharted proposal).
  const defaultDueDaysRef = useRef<number | null>(null);
  const dueDateSetByHandRef = useRef(Boolean(initialValues?.dueDate));
  // Client team (Mg-231): the assignee the window picked by itself (person id,
  // "" = nobody). While the assignee is still that pick, choosing another
  // client / work type may change it; once the person changes it, it stays.
  const autoAssigneeRef = useRef<string>(
    initialValues?.assignedTo ? FIXED : user?.id || "",
  );
  // The chosen work type's defaults (null = none / not loaded).
  const workTypeDefaultsRef = useRef<{
    assignee: SelectOptionType | null;
    hasRole: boolean;
  } | null>(null);

  const searchContactsDebounce = useDebounce(searchContacts, 200);
  const searchTypeDebounce = useDebounce(searchType, 200);
  const searchTemplateDebounce = useDebounce(searchTemplate, 200);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useForm<AddWorkFormData>({
    defaultValues: {
      ...buildDefaultValues(user, defaultContact, defaultContacts),
      ...initialValues,
    },
  });

  const applyDefaultDueDate = useCallback(() => {
    if (dueDateSetByHandRef.current) {
      return;
    }

    const start = getValues("startDate");
    const due = defaultDueDateFromStart(
      start ? moment(start).toDate() : null,
      defaultDueDaysRef.current,
    );

    if (due) {
      setValue("dueDate", moment(due).toISOString());
    }
  }, [getValues, setValue]);

  useEffect(() => {
    const subscription = watch((_values, { name, type }) => {
      // type "change" = the person used the field (setValue has no type).
      if (type !== "change") {
        return;
      }

      if (name === "dueDate") {
        dueDateSetByHandRef.current = true;
      } else if (name === "startDate") {
        applyDefaultDueDate();
      }
    });

    return () => subscription.unsubscribe();
  }, [applyDefaultDueDate, watch]);

  const {
    data: contacts,
    isLoading: isLoadingContacts,
    isFetching: isFetchingContacts,
  } = useGetActiveCustomersQuery(
    {
      orgId,
      isActive: true,
      userId: user?.id || "",
      tagIds: [],
      yearEndMonth: [],
      customerName: null,
      sortOrder: CONTACTS_SORT_ORDER,
      sortColumn: CONTACTS_SORT_COLUMN,
      searchQuery: searchContactsDebounce || "",
      pageSize: CONTACTS_PAGE_SIZE,
      pageNumber: contactsPage,
      organizationTypeIds: [],
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !orgId || !user?.id,
    },
  );

  const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery(orgId, {
    skip: !orgId,
  });
  const { data: contactTags, isFetching: isLoadingContactTags } =
    useGetTagsQuery(
      { orgId },
      {
        skip: !orgId,
      },
    );
  const { data: roles, isLoading: isLoadingRoles } = useGetRolesQuery(
    { orgId: orgId || "" },
    { skip: !orgId },
  );
  const [getWorkTypes, { data: workTypes, isFetching: isLoadingWorkTypes }] =
    useLazySearchWorkTypesQuery();
  const [
    getWorkTypeDetails,
    { data: selectedWorkTypeDetails, isFetching: isLoadingWorkTypeDefaults },
  ] = useLazyGetWorkTypeQuery();
  const [getTemplates, { data: templates, isFetching: isLoadingTemplates }] =
    useLazySearchTemplateQuery();

  useEffect(() => {
    if (!orgId) {
      return;
    }

    getWorkTypes({
      orgId,
      query: searchTypeDebounce || null,
    });
  }, [getWorkTypes, orgId, searchTypeDebounce]);

  useEffect(() => {
    if (!orgId) {
      return;
    }

    getTemplates({
      orgId,
      query: searchTemplateDebounce || null,
    });
  }, [getTemplates, orgId, searchTemplateDebounce]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    reset({
      ...buildDefaultValues(user, defaultContact, defaultContacts),
      ...initialValues,
    });
    defaultDueDaysRef.current = null;
    dueDateSetByHandRef.current = Boolean(initialValues?.dueDate);
    autoAssigneeRef.current = initialValues?.assignedTo
      ? FIXED
      : user?.id || "";
    workTypeDefaultsRef.current = null;
    setNoteDraft(initialNote || "");
    setSubmitError(null);
    setAutomationDraft(initialAutomation ?? null);
    setSequenceDraft(initialSequence ?? null);
    setRepeatDraft(initialRepeat ?? null);
    setSelectedAutomationTarget(null);
    setContactSelectTab("contacts");
    setSelectedContactTag(null);
  }, [
    defaultContact,
    defaultContacts,
    initialAutomation,
    initialNote,
    initialRepeat,
    initialSequence,
    initialValues,
    isOpen,
    reset,
    user,
  ]);

  useEffect(() => {
    if (!isFetchingContacts) {
      isLoadingNextContactsPageRef.current = false;
    }
  }, [isFetchingContacts]);

  useEffect(() => {
    setContactsPage(1);
    setContactOptions([]);
    isLoadingNextContactsPageRef.current = false;
  }, [searchContactsDebounce, orgId, user?.id]);

  useEffect(() => {
    const nextOptions = (contacts || [])
      .map(mapCustomerToContactOption)
      .filter((option) => option.value);

    setContactOptions((prev) =>
      contactsPage === 1
        ? uniqueOptionsByValue(nextOptions)
        : uniqueOptionsByValue([...prev, ...nextOptions]),
    );
  }, [contacts, contactsPage]);

  const selectedWorkType = watch("workType");
  const selectedContacts = watch("contact") || [];
  const selectedCustomerKey = selectedContacts
    .map((contact) => String(contact.value || ""))
    .filter(Boolean)
    .join(",");
  const selectedCustomerIds = useMemo(
    () => (selectedCustomerKey ? selectedCustomerKey.split(",") : []),
    [selectedCustomerKey],
  );
  const { currentData: clientTeams } = useLookupClientTeamsQuery(
    { orgId, customerIds: selectedCustomerIds },
    { skip: !isOpen || !orgId || !selectedCustomerIds.length },
  );
  const clientManager = useMemo(
    () => sharedManager(clientTeams?.rows, selectedCustomerIds),
    [clientTeams, selectedCustomerIds],
  );
  const clientManagerRef = useRef(clientManager);
  clientManagerRef.current = clientManager;
  const primarySelectedContact = selectedContacts[0] || null;
  const automationLookups = useAutomationLookups(
    selectedWorkType?.value || undefined,
  );
  const activeWorkTypeDetails =
    selectedWorkTypeDetails?.id === selectedWorkType?.value
      ? selectedWorkTypeDetails
      : undefined;

  const createdBy = useMemo(() => {
    const fullName = `${user?.userProfile?.firstName || ""} ${
      user?.userProfile?.lastName || ""
    }`.trim();

    return fullName || "Unknown user";
  }, [user?.userProfile?.firstName, user?.userProfile?.lastName]);

  const automationTarget = useMemo<AutomationTarget>(() => {
    return {
      type: "work",
      id: "new-work",
      title: "work item",
      entityLabel: "work item",
      hideTitleInPreview: true,
      work: {
        id: "new-work",
        name: "work item",
        orgId,
        serviceId: selectedWorkType?.value || "",
        customerId: primarySelectedContact?.value || "",
        businessContactId: null,
        customer: {
          id: primarySelectedContact?.value || "",
        },
      } as WorkDetailsModel,
    };
  }, [orgId, primarySelectedContact?.value, selectedWorkType?.value]);

  const contactsTotalRecords = contacts?.[0]?.totalRecords || 0;
  const hasMoreContacts =
    contactsTotalRecords > 0
      ? contactOptions.length < contactsTotalRecords
      : (contacts?.length || 0) === CONTACTS_PAGE_SIZE;
  const isLoadingContactsFirstPage =
    (isLoadingContacts || isFetchingContacts) && contactsPage === 1;
  const isLoadingMoreContacts = isFetchingContacts && contactsPage > 1;

  const contactsOptions = useMemo<AddWorkContactOption[]>(
    () =>
      uniqueOptionsByValue([
        ...(defaultContact
          ? [mapLegacyContactToContactOption(defaultContact)]
          : []),
        ...(defaultContacts || []).map(mapLegacyContactToContactOption),
        ...contactOptions,
      ]),
    [contactOptions, defaultContact, defaultContacts],
  );

  const contactTagOptions = useMemo<AddWorkContactTagOption[]>(
    () =>
      (contactTags || []).map((tag) => {
        const tagContacts = uniqueOptionsByValue(
          (tag.tagCustomers || []).map(mapTagCustomerToContactOption),
        );
        const count = tagContacts.length || tag.counts || 0;

        return {
          value: tag.id,
          label: tag.tagName,
          caption: `${count} ${count === 1 ? "contact" : "contacts"}`,
          contacts: tagContacts,
          count,
        };
      }),
    [contactTags],
  );

  const handleLoadMoreContacts = useCallback(() => {
    if (
      !hasMoreContacts ||
      isFetchingContacts ||
      isLoadingNextContactsPageRef.current
    ) {
      return;
    }

    isLoadingNextContactsPageRef.current = true;
    setContactsPage((prev) => prev + 1);
  }, [hasMoreContacts, isFetchingContacts]);

  const usersOptions = useMemo<SelectOptionType[]>(() => {
    const options: SelectOptionType[] = (users || []).map((item) => ({
      avatar: getInitialsFromFullName(
        `${item?.userProfile?.firstName} ${item?.userProfile?.lastName}`,
      ),
      label: `${item?.userProfile?.firstName} ${item?.userProfile?.lastName}`,
      value: `${item?.id}`,
      type: "custom",
      avatarCustomColor: item.userProfile.color,
    }));
    const defaultAssignee =
      activeWorkTypeDetails?.defaultWorkTypeData?.defaultAssignee;

    if (
      defaultAssignee &&
      !options.some((option) => option.value === defaultAssignee.id)
    ) {
      const firstName = defaultAssignee.userProfile?.firstName || "";
      const lastName = defaultAssignee.userProfile?.lastName || "";
      const fullName = `${firstName} ${lastName}`.trim();
      const label = fullName || defaultAssignee.userProfile?.email || "Unknown";

      return [
        {
          avatar: getInitialsFromFullName(label),
          label,
          value: defaultAssignee.id,
          type: "custom",
          avatarCustomColor: defaultAssignee.userProfile?.color || undefined,
        },
        ...options,
      ];
    }

    return options;
  }, [activeWorkTypeDetails?.defaultWorkTypeData?.defaultAssignee, users]);

  const workTypeOptions = useMemo<SelectOptionType[]>(() => {
    return (
      workTypes
        ?.map((item) => ({
          label: item?.name ?? "",
          value: item?.id,
        }))
        .sort(compareSelectOptionsAlphabeticallyWithNumbersFirst) || []
    );
  }, [workTypes]);

  const templateOptions = useMemo<SelectOptionType[]>(() => {
    const options =
      templates
        ?.map((item) => ({
          value: item?.id,
          label: item?.templateName ?? "",
        }))
        .sort(compareSelectOptionsAlphabeticallyWithNumbersFirst) || [];
    const defaultTemplate =
      activeWorkTypeDetails?.defaultWorkTypeData?.defaultTemplate;

    if (
      defaultTemplate &&
      !options.some((option) => option.value === defaultTemplate.id)
    ) {
      return [
        {
          value: defaultTemplate.id,
          label: defaultTemplate.templateName,
        },
        ...options,
      ];
    }

    return options;
  }, [activeWorkTypeDetails?.defaultWorkTypeData?.defaultTemplate, templates]);

  const roleOptions = useMemo<SelectOptionType[]>(() => {
    const options = (roles || []).map((role) => ({
      value: role.id,
      label: role.roleName,
    }));
    const defaultRole = activeWorkTypeDetails?.defaultWorkTypeData?.defaultRole;

    if (
      defaultRole &&
      !options.some((option) => option.value === defaultRole.id)
    ) {
      options.unshift({
        value: defaultRole.id,
        label: defaultRole.roleName,
      });
    }

    return [NO_ROLE_OPTION, ...options];
  }, [activeWorkTypeDetails?.defaultWorkTypeData?.defaultRole, roles]);

  const renderRoleMembersPreview = useCallback(
    (option: SelectOptionType) => {
      if (!option.value) {
        return null;
      }

      const role = getSelectedRole(roles, option.value);

      return <RoleMembersPreview role={role} />;
    },
    [roles],
  );

  /** The window's own assignee pick (work type default > client manager > role queue > me). */
  const autoAssignees = useCallback((): SelectOptionType[] => {
    const defaults = workTypeDefaultsRef.current;
    const manager = clientManagerRef.current;
    const choice = chooseDefaultAssignee({
      workTypeAssigneeId: defaults?.assignee?.value
        ? String(defaults.assignee.value)
        : null,
      hasDefaultRole: defaults?.hasRole,
      clientManagerId: manager?.id,
      meId: user?.id,
    });
    autoAssigneeRef.current = choice.id || "";
    if (choice.source === "workType" && defaults?.assignee)
      return [defaults.assignee];
    if (choice.source === "clientManager" && manager) {
      return [
        findOptionByValue(usersOptions, manager.id) ?? {
          avatar: getInitialsFromFullName(manager.name),
          label: manager.name,
          value: manager.id,
          type: "custom" as const,
          avatarCustomColor: manager.color || undefined,
        },
      ];
    }
    if (choice.source === "me" && user?.id) return [buildUserOption(user)];
    return [];
  }, [user, usersOptions]);

  // A client (or a client's manager) changed: follow it while the assignee is
  // still the window's own pick.
  useEffect(() => {
    if (!isOpen || autoAssigneeRef.current === FIXED) return;
    const current = (getValues("assignedTo") || []).map((o) => String(o.value));
    const auto = autoAssigneeRef.current;
    const stillAuto = auto
      ? current.length === 1 && current[0] === auto
      : !current.length;
    if (!stillAuto) return;
    const next = autoAssignees();
    const nextIds = next.map((o) => String(o.value));
    if (nextIds.join(",") !== current.join(",")) {
      setValue("assignedTo", next);
      void trigger(["assignedTo", "role"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientManager?.id, isOpen]);

  const handleWorkTypeChange = useCallback(
    async (
      newWorkType: SelectOptionType | null,
      onChange: (value: SelectOptionType | null) => void,
    ) => {
      const requestId = defaultWorkTypeRequestIdRef.current + 1;
      defaultWorkTypeRequestIdRef.current = requestId;

      onChange(newWorkType);
      setSubmitError(null);
      setValue("subtaskTemplate", null);
      setValue("role", null);
      setValue("budgetedTime", "0");
      workTypeDefaultsRef.current = null;
      setValue("assignedTo", autoAssignees());

      defaultDueDaysRef.current = null;

      if (!newWorkType?.value) {
        void trigger(["assignedTo", "role"]);
        return;
      }

      try {
        const workType = await getWorkTypeDetails({
          id: newWorkType.value,
        }).unwrap();

        if (defaultWorkTypeRequestIdRef.current !== requestId) {
          return;
        }

        defaultDueDaysRef.current = validDueDays(workType.noOfDays);
        applyDefaultDueDate();

        const defaultData = workType.defaultWorkTypeData;

        if (!defaultData) {
          void trigger(["assignedTo", "role"]);
          return;
        }

        const defaultTemplateOption =
          findOptionByValue(templateOptions, defaultData.defaultTemplateId) ??
          (defaultData.defaultTemplate && defaultData.defaultTemplateId
            ? {
                value: defaultData.defaultTemplateId,
                label: defaultData.defaultTemplate.templateName,
              }
            : null);
        const defaultAssignee = defaultData.defaultAssignee;
        const defaultAssigneeName = defaultAssignee
          ? `${defaultAssignee.userProfile?.firstName || ""} ${
              defaultAssignee.userProfile?.lastName || ""
            }`.trim()
          : "";
        const defaultAssigneeLabel =
          defaultAssigneeName ||
          defaultAssignee?.userProfile?.email ||
          "Unknown";
        const defaultAssigneeOption =
          findOptionByValue(usersOptions, defaultData.defaultAssigneeId) ??
          (defaultAssignee && defaultData.defaultAssigneeId
            ? {
                avatar: getInitialsFromFullName(defaultAssigneeLabel),
                label: defaultAssigneeLabel,
                value: defaultData.defaultAssigneeId,
                type: "custom" as const,
                avatarCustomColor:
                  defaultAssignee.userProfile?.color || undefined,
              }
            : null);
        const defaultRoleOption =
          findOptionByValue(roleOptions, defaultData.defaultRoleId) ??
          (defaultData.defaultRole && defaultData.defaultRoleId
            ? {
                value: defaultData.defaultRoleId,
                label: defaultData.defaultRole.roleName,
              }
            : null);

        setValue("subtaskTemplate", defaultTemplateOption);
        setValue("role", defaultRoleOption);
        // Client team (owner 2026-10-31): the work type's own default
        // assignee wins, then the client manager, then the role queue / me.
        workTypeDefaultsRef.current = {
          assignee: defaultAssigneeOption,
          hasRole: !!defaultRoleOption,
        };
        setValue("assignedTo", autoAssignees());
        setValue(
          "budgetedTime",
          defaultData.defaultBudget === null ||
            defaultData.defaultBudget === undefined
            ? "0"
            : String(defaultData.defaultBudget),
        );
        void trigger(["assignedTo", "role"]);
      } catch {
        if (defaultWorkTypeRequestIdRef.current === requestId) {
          setSubmitError("Failed to load work type defaults.");
        }
      }
    },
    [
      applyDefaultDueDate,
      autoAssignees,
      getWorkTypeDetails,
      roleOptions,
      setValue,
      templateOptions,
      trigger,
      usersOptions,
    ],
  );

  const hasRoleOrAssignee = useCallback(() => {
    const role = getValues("role");
    const assignees = getValues("assignedTo") || [];

    return !!role?.value || assignees.length > 0;
  }, [getValues]);

  const buildAutomationValues = useCallback(
    (draft: AutomationDraft) => {
      const conditionValue = getAutomationConditionBodyValue({
        conditionKey: draft.conditionKey,
        conditionValue: draft.conditionValue,
        conditionExtraValue: draft.conditionExtraValue,
        workStatusOptions: automationLookups.options.workStatuses,
        subtaskStatusOptions: automationLookups.options.subtaskStatuses,
      });
      const actionValue = getAutomationActionBodyValue({
        actionKey: draft.actionKey,
        actionValue: draft.actionValue,
        actionExtraValue: draft.actionExtraValue,
        workStatusOptions: automationLookups.options.workStatuses,
        subtaskStatusOptions: automationLookups.options.subtaskStatuses,
      });

      return {
        conditionValue,
        actionValue,
      };
    },
    [
      automationLookups.options.subtaskStatuses,
      automationLookups.options.workStatuses,
    ],
  );

  const automationPreview = useMemo<AutomationItem | null>(() => {
    if (!automationDraft?.conditionId || !automationDraft.actionId) {
      return null;
    }

    const { conditionValue, actionValue } =
      buildAutomationValues(automationDraft);

    return {
      id: "new-work-automation-preview",
      scope: "work",
      workItemId: "new-work",
      workItemTitle: "work item",
      conditionId: automationDraft.conditionId,
      conditionKey: automationDraft.conditionKey,
      conditionValue,
      actionId: automationDraft.actionId,
      actionKey: automationDraft.actionKey,
      actionValue,
      createdBy,
      createdAt: new Date().toISOString(),
      lastRunStatus: "idle",
    };
  }, [automationDraft, buildAutomationValues, createdBy]);

  const handleOpenAutomationModal = useCallback(() => {
    setSelectedAutomationTarget(automationTarget);
  }, [automationTarget]);

  const handleCloseAutomationModal = useCallback(() => {
    setSelectedAutomationTarget(null);
  }, []);

  const handleSaveAutomationDraft = useCallback(
    async (draft: AutomationDraft) => {
      setAutomationDraft(draft);
      setSelectedAutomationTarget(null);
    },
    [],
  );

  const handleDeleteAutomationDraft = useCallback(() => {
    setAutomationDraft(null);
  }, []);

  const createAutomationForWork = useCallback(
    async (workId: string, draft: AutomationDraft) => {
      if (!draft.conditionId || !draft.actionId) {
        return;
      }

      const { conditionValue, actionValue } = buildAutomationValues(draft);

      await addAutomation({
        orgId,
        workId,
        createdById: user?.id || "",
        conditionId: draft.conditionId,
        actionId: draft.actionId,
        isActive: true,
        isExecuted: false,
        isSuccess: false,
        conditionValue,
        actionValue,
      }).unwrap();
    },
    [addAutomation, buildAutomationValues, orgId, user?.id],
  );

  const onSubmit = async (data: AddWorkFormData) => {
    if (!user?.id || !orgId) {
      setSubmitError("Unable to create work without an active organization.");
      return;
    }

    if (moment(data.startDate).isAfter(moment(data.dueDate), "day")) {
      setIsInvalidDatesModalOpen(true);
      return;
    }

    setSubmitError(null);

    try {
      const addWorkResponse = await addWork(
        buildAddWorkDto({
          data,
          orgId,
          userId: user.id,
        }),
      ).unwrap();
      const createdWorkIds = getCreatedWorkIds(addWorkResponse);
      const firstCreatedWorkId = createdWorkIds[0];

      if (!firstCreatedWorkId) {
        throw new Error("Add work response did not include a work id.");
      }

      // Automation first: starting the repeat can create the next work items at
      // once ("Immediately", or a specific date that is today), and each copy
      // takes the automations the work has at that moment.
      if (automationDraft) {
        try {
          await Promise.all(
            createdWorkIds.map((workId) =>
              createAutomationForWork(workId, automationDraft),
            ),
          );
        } catch {
          toast.error("Work was created, but automation was not added.");
        }
      }

      if (repeatDraft) {
        // Built now, so the schedule follows the dates in the final form.
        const repeat = buildRepeatPayload(repeatDraft, {
          startDate: data.startDate,
          dueDate: data.dueDate,
        });
        const results = repeat.ok
          ? await Promise.allSettled(
              createdWorkIds.map((workId) =>
                updateWorkRepeats({ workId, ...repeat.payload }).unwrap(),
              ),
            )
          : [];
        if (
          !repeat.ok ||
          results.some((result) => result.status === "rejected")
        ) {
          toast.error(
            "Work was created, but the repeat schedule was not started. You can start it from the work item's Repeat tab.",
          );
        }
      }

      if (sequenceDraft) {
        // Several contacts = several work items; each gets the sequence,
        // sent to that contact's primary email (blank address).
        const toEmail =
          (data.contact || []).length > 1 ? "" : sequenceDraft.toEmail;
        const results = await Promise.allSettled(
          createdWorkIds.map((workId) =>
            startSequence(
              buildSequencePayload(
                { ...sequenceDraft, toEmail },
                { orgId, workId, userId: user.id },
              ),
            ).unwrap(),
          ),
        );
        if (results.some((result) => result.status === "rejected")) {
          toast.error(
            "Work was created, but the email sequence was not started for every work item.",
          );
        }
      }

      const note = noteDraft.trim();
      if (initialNote !== undefined && note) {
        // Same call as the work Notes chat; a failure doesn't undo the work.
        const results = await Promise.allSettled(
          createdWorkIds.map((workId) =>
            addWorkNote({
              title: "",
              note,
              createdByUserRelayId: `${userInfoByEmail?.id || user.id}`,
              workId,
            }).unwrap(),
          ),
        );
        if (results.some((result) => result.status === "rejected")) {
          toast.error(
            "Work was created, but the note was not added. You can add it in the work item's Notes.",
          );
        }
      }

      if (selectedMail) {
        try {
          await Promise.all(
            createdWorkIds.map((workId) =>
              addWorkToEmail({
                body: {
                  messageId: selectedMail.id || "",
                  threadId: selectedMail.thread_id || "",
                  workId,
                },
              }).unwrap(),
            ),
          );
        } catch {
          console.error("Failed to link email to created work");
        }
      }

      onCreated?.(createdWorkIds);
      onClose();
      navigate(`/work/${firstCreatedWorkId}`);
    } catch (error) {
      console.error("Failed to create work");
      // The server's own sentence for rules the person can fix (e.g. "This client is
      // archived. Reactivate it first.", owner 2026-10-22).
      setSubmitError(
        serverMessage(error, "Failed to create work. Please try again."),
      );
    }
  };

  const selectedAssignees = watch("assignedTo") || [];
  const selectedRole = watch("role") || null;
  const selectedAssigneeItems = getSelectedAssigneeItems(selectedAssignees);
  const selectedRoleItems = useMemo<AvatarItem[]>(() => {
    const members =
      getSelectedRole(roles, selectedRole?.value || null)?.userRoles || [];

    return members.map((member) => ({
      id: member.id,
      label: getInitials(
        member.user.userProfile.firstName,
        member.user.userProfile.lastName,
      ),
      fullName: `${member.user.userProfile.firstName} ${member.user.userProfile.lastName}`,
      color: member.user.userProfile.color,
    }));
  }, [roles, selectedRole]);

  return {
    noteDraft,
    setNoteDraft,
    showNote: initialNote !== undefined,
    sequenceDraft,
    setSequenceDraft,
    repeatDraft,
    setRepeatDraft,
    workDates: { startDate: watch("startDate"), dueDate: watch("dueDate") },
    selectedContacts,
    selectedWorkType,
    currentUserId: user?.id,
    automationLookups,
    automationPreview,
    control,
    createdBy,
    contactSelectTab,
    contactTagOptions,
    contactsOptions,
    errors,
    handleCloseAutomationModal,
    handleDeleteAutomationDraft,
    handleLoadMoreContacts,
    handleOpenAutomationModal,
    handleSaveAutomationDraft,
    handleSubmit,
    handleWorkTypeChange,
    hasRoleOrAssignee,
    hasMoreContacts,
    isAddingWork:
      isAddingWork ||
      isAddingAutomation ||
      isStartingSequence ||
      isStartingRepeat,
    isInvalidDatesModalOpen,
    isLoadingContactsFirstPage,
    isLoadingContactTags,
    isLoadingMoreContacts,
    isLoadingRoles,
    isLoadingTemplates,
    isLoadingUsers,
    isLoadingWorkTypeDefaults,
    isLoadingWorkTypes,
    onSubmit,
    register,
    renderRoleMembersPreview,
    roleOptions,
    searchContacts,
    searchTemplate,
    searchType,
    selectedAssigneeItems,
    selectedAutomationTarget,
    selectedContactTag,
    selectedRoleItems,
    setContactSelectTab,
    setIsInvalidDatesModalOpen,
    setSearchContacts,
    setSearchTemplate,
    setSearchType,
    setSelectedContactTag,
    submitError,
    templateOptions,
    trigger,
    usersOptions,
    workTypeOptions,
  };
};
